import { useState, useEffect } from 'react';
import { CreditCard, Plus, Trash2 } from 'lucide-react';
import { PageShell, Modal, FG, INP, SEL, Row2, BtnPrimary, BtnGhost, Spinner, useBanner, useWalletUser, EmptyState, maskCard } from './WalletShared';
import walletController from '../../controllers/walletController';

const CARD_COLORS = ['#1e293b','#1d4ed8','#15803d','#6d28d9','#92400e'];

const Cards = () => {
  const { userId } = useWalletUser();
  const { banner, notify, clearBanner } = useBanner();
  const [cards,    setCards]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showAdd,  setShowAdd]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form,     setForm]     = useState({ cardHolderName:'', cardNumber:'', expiryMonth:'', expiryYear:'', cardType:'debit', network:'visa' });
  const f = (k,v) => setForm(p => ({ ...p, [k]:v }));

  const load = async () => {
    setLoading(true);
    const res = await walletController.getCards(userId);
    if (res.success) setCards(res.data);
    else notify('error', res.message);
    setLoading(false);
  };
  useEffect(() => { load(); }, [userId]);

  const handleAdd = async () => {
    if (!form.cardNumber||!form.cardHolderName||!form.expiryMonth||!form.expiryYear) return notify('error','All card fields are required');
    setSaving(true);
    const res = await walletController.addCard(userId, form);
    setSaving(false);
    if (res.success) { notify('success', res.message); setShowAdd(false); load(); }
    else notify('error', res.message);
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    const res = await walletController.deleteCard(userId, id);
    setDeleting(null);
    if (res.success) { notify('success', res.message); load(); }
    else notify('error', res.message);
  };

  return (
    <PageShell title="Cards" subtitle="Manage your debit and credit cards" icon={CreditCard}
      banner={banner} onCloseBanner={clearBanner}
      actions={<button onClick={() => setShowAdd(true)} style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 14px', background:'#1e293b', color:'#fff', border:'none', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer' }}><Plus size={14}/>Add Card</button>}>

      {loading ? <div style={{ display:'flex', justifyContent:'center', padding:'50px 0', gap:10, color:'#64748b' }}><Spinner size={24}/></div>
        : cards.length===0 ? <EmptyState icon={CreditCard} message="No cards added" action={<button onClick={() => setShowAdd(true)} style={{ padding:'8px 18px', background:'#1e293b', color:'#fff', border:'none', borderRadius:8, fontSize:13, cursor:'pointer' }}>Add Card</button>}/>
        : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:16 }}>
            {cards.map((c,i) => (
              <div key={c._id} style={{ borderRadius:16, padding:'22px 24px', background:CARD_COLORS[i%CARD_COLORS.length], color:'#fff', position:'relative', minHeight:160 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
                  <span style={{ fontSize:12, opacity:.7, textTransform:'capitalize' }}>{c.network?.toUpperCase()||'VISA'}</span>
                  <button onClick={() => handleDelete(c._id)} disabled={deleting===c._id}
                    style={{ background:'rgba(255,255,255,.15)', border:'none', borderRadius:6, width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#fff' }}>
                    {deleting===c._id ? <Spinner size={12}/> : <Trash2 size={12}/>}
                  </button>
                </div>
                <div style={{ fontSize:18, fontWeight:700, letterSpacing:2, marginBottom:20 }}>{maskCard(c.cardNumber)}</div>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:12 }}>
                  <div><div style={{ opacity:.6, marginBottom:2 }}>Card Holder</div><div style={{ fontWeight:600 }}>{c.cardHolderName}</div></div>
                  <div><div style={{ opacity:.6, marginBottom:2 }}>Expires</div><div style={{ fontWeight:600 }}>{c.expiryMonth}/{String(c.expiryYear).slice(-2)}</div></div>
                  <div><div style={{ opacity:.6, marginBottom:2 }}>Type</div><div style={{ fontWeight:600, textTransform:'capitalize' }}>{c.cardType}</div></div>
                </div>
              </div>
            ))}
          </div>
        )}

      {showAdd && (
        <Modal title="Add New Card" onClose={() => setShowAdd(false)}
          footer={<><BtnGhost onClick={() => setShowAdd(false)}>Cancel</BtnGhost><BtnPrimary onClick={handleAdd} disabled={saving}>{saving?<><Spinner size={13}/>Adding…</>:<><Plus size={13}/>Add Card</>}</BtnPrimary></>}>
          <FG label="Card Holder Name *"><INP value={form.cardHolderName} onChange={e => f('cardHolderName',e.target.value)} placeholder="Name on card"/></FG>
          <FG label="Card Number *"><INP value={form.cardNumber} onChange={e => f('cardNumber',e.target.value.replace(/\D/g,'').slice(0,16))} placeholder="16-digit card number" maxLength={16}/></FG>
          <Row2>
            <Row2>
              <FG label="Expiry Month"><INP type="number" min="1" max="12" value={form.expiryMonth} onChange={e => f('expiryMonth',e.target.value)} placeholder="MM"/></FG>
              <FG label="Expiry Year"><INP type="number" min={new Date().getFullYear()} max={2040} value={form.expiryYear} onChange={e => f('expiryYear',e.target.value)} placeholder="YYYY"/></FG>
            </Row2>
            <Row2>
              <FG label="Type">
                <SEL value={form.cardType} onChange={e => f('cardType',e.target.value)}>
                  <option value="debit">Debit</option>
                  <option value="credit">Credit</option>
                  <option value="prepaid">Prepaid</option>
                </SEL>
              </FG>
              <FG label="Network">
                <SEL value={form.network} onChange={e => f('network',e.target.value)}>
                  <option value="visa">Visa</option>
                  <option value="mastercard">Mastercard</option>
                  <option value="rupay">RuPay</option>
                  <option value="amex">Amex</option>
                </SEL>
              </FG>
            </Row2>
          </Row2>
        </Modal>
      )}
    </PageShell>
  );
};
export default Cards;